import { getDefaultOptions, getIndex } from "@Components/assessment//helpers";
import { BLANK_ITEM, BLANK_TEMPLATE } from "@Components/assessment/assessmentConstants";
import { useCreateTemplate, useDeleteTemplate, useUpdateTemplate } from "@Components/assessment/mutations";
import { ISortableItemProps } from "@Components/shared/SortableList";
import { QueryKeys } from "@Constants";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { useQueryClient } from "@tanstack/react-query";
import { clone } from "@Utils";
import { FormInstance, InputRef, notification } from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { useEffect, useState } from "react";

interface IUseTemplateDesigner {
    form: FormInstance;
    groupTemplates?: IFeedbackTemplateDetails[];
    nameInputRef: React.MutableRefObject<InputRef>;
    templateDetails?: IFeedbackTemplateDetails;
}

const useTemplateDesigner = ({ form, groupTemplates, nameInputRef, templateDetails }: IUseTemplateDesigner) => {
    const [items, setItems] = useState<ISortableItemProps[]>([BLANK_ITEM]);

    const [template, setTemplate] = useState<IFeedbackTemplateDetails>(
        templateDetails ? templateDetails : clone(BLANK_TEMPLATE)
    );

    const [selectedTemplateId, setSelectedTemplateId] = useState<string>();

    const createTemplate = useCreateTemplate();
    const deleteTemplate = useDeleteTemplate();
    const updateTemplate = useUpdateTemplate();

    const queryClient = useQueryClient();

    const clearForm = () => {
        setTemplate(clone(BLANK_TEMPLATE));
        setItems([BLANK_ITEM]);
        form.resetFields();
        const { description, name } = clone(BLANK_TEMPLATE);
        form.setFieldsValue({ description, name });
        setSelectedTemplateId(undefined);
    };

    const debounceUpdatePromise = AwesomeDebouncePromise(async () => {
        if (template?.templateId) await handleEditFormSubmit(false);
    }, 1000);

    const handleAdd = () => {
        const newTemplate = { ...template };
        const newId = template?.fields?.length.toString();
        const fields = [...template.fields];

        const newField = {
            id: newId,
            name: "New field",
            type: "text",
            defaultValue: "",
            options: getDefaultOptions("text"),
        };
        fields.push(newField);
        newTemplate.fields = fields;
        setTemplate(newTemplate);

        const newItems = [...items];
        newItems.push({ id: newId });
        setItems(newItems);
    };

    const handleDeleteRow = async (id: string) => {
        const newTemplate = { ...template };
        newTemplate.fields = template?.fields?.filter((x) => x.id !== id);
        setTemplate(newTemplate);
        const newItems = items.filter((x) => x.id !== id);
        setItems(newItems);
        await debounceUpdatePromise();
    };

    const handleDeleteTemplate = async () => {
        const { templateId: existingTemplateId } = template;
        if (existingTemplateId && !template.locked) {
            const existingTemplate = groupTemplates.find((gTemplate) => gTemplate.templateId === existingTemplateId);
            const { groupId } = existingTemplate;
            await deleteTemplate.mutateAsync({ groupId, templateId: existingTemplateId }).then(() => {
                notification.success({ message: "Template deleted." });
                clearForm();
            });
        }
        clearForm();
    };

    const handleDuplicateClick = async () => {
        await handleEditFormSubmit(true);
    };

    const handleEditFormSubmit = async (shouldDuplicate = false) => {
        const { description, groupId, name } = form.getFieldsValue();
        const model = { ...template, description, groupId, name };
        return !shouldDuplicate && template.templateId
            ? await updateTemplate.mutateAsync({ groupId: groupId, model }).then((res) => {
                  notification.success({ message: "Template saved." });
                  return res;
              })
            : await createTemplate.mutateAsync({ groupId: groupId, model }).then((res) => {
                  queryClient.invalidateQueries([QueryKeys.Templates]);
                  setSelectedTemplateId(res.value.id);
                  notification.success({ message: `Template ${shouldDuplicate ? "duplicated" : "created"}.` });
                  return res;
              });
    };

    const handleNameChange = async (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
        const newTemplate = { ...template };
        const fields = [...template.fields];
        fields[getIndex(id, fields)].name = e.target.value;
        newTemplate.fields = fields;
        await debounceUpdatePromise();
        setTemplate(newTemplate);
    };

    const handleNewClick = () => clearForm();

    const handleOptionArrayChange = async (id: string, values: string[]) => {
        const newTemplate = { ...template };
        const fields = [...template.fields];
        fields[getIndex(id, fields)].options = values;
        newTemplate.fields = fields;
        setTemplate(newTemplate);
    };

    const handleOptionChange = async (id: string, option: number, value: string) => {
        const newTemplate = { ...template };
        const fields = [...template.fields];
        fields[getIndex(id, fields)].options[option] = value;
        newTemplate.fields = fields;
        await debounceUpdatePromise();
        setTemplate(newTemplate);
    };

    const handleTemplateClear = () => clearForm();

    const handleTemplateSelect = (id: IFeedbackTemplateDetails["templateId"]) => setSelectedTemplateId(id);

    const handleTypeChange = async (id: string, newType: string) => {
        const newTemplate = { ...template };
        const fields = [...template.fields];
        fields[getIndex(id, fields)].type = newType;
        fields[getIndex(id, fields)].options = getDefaultOptions(newType);
        newTemplate.fields = fields;
        setTemplate(newTemplate);
    };

    useEffect(() => {
        // this updates the dropdown and name field with the values on the newly duplicated template
        if (!selectedTemplateId || !nameInputRef.current || template?.templateId === selectedTemplateId) return;

        const selectedTemplate = groupTemplates.find(
            (groupTemplate) => groupTemplate.templateId === selectedTemplateId
        );
        if (!selectedTemplate) return;

        setTemplate(selectedTemplate);
        const { locked, fields, ...fieldsValue } = selectedTemplate;
        form.setFieldsValue(fieldsValue);
        nameInputRef.current.focus();
        setItems(fields);
    }, [form, groupTemplates, nameInputRef, selectedTemplateId, template?.templateId]);

    return {
        handleAdd,
        handleDebouncedEditFormSubmit: debounceUpdatePromise,
        handleDeleteRow,
        handleDeleteTemplate,
        handleDuplicateClick,
        handleEditFormSubmit,
        handleNameChange,
        handleNewClick,
        handleOptionArrayChange,
        handleOptionChange,
        handleTemplateClear,
        handleTemplateSelect,
        handleTypeChange,
        items,
        selectedTemplateId,
        setItems,
        template,
    };
};

export default useTemplateDesigner;
